import React, { useCallback, useMemo } from "react";
import { withIssuesConsumer } from "@adsk/acc-issues-common/components/primitive/IssuesConsumer";
import { useIssuesTableData } from "../IssuesTab/IssueLog/IssueTable/useIssuesTableData";
import { Issue } from "@adsk/acc-issues-sdk";
import { useReactTable, getCoreRowModel, Row } from "@tanstack/react-table";
import { keyBy } from "@adsk/alloy-react-helpers";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import {
  useTable,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderGroup,
  LoadMoreDataFooter,
} from "@adsk/alloy-react-table";
import theme from "@adsk/alloy-react-theme";

import { useTableColumns, useVirtualTable } from "./hooks";
import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import { useTranslations } from "@adsk/acc-issues-translations";
import { ENTITIES_MAPPER_KEY } from "./constants";
import styled from "styled-components";

const StyledLoadMoreDataFooter = styled(LoadMoreDataFooter)`
  padding-left: 50px;
  justify-content: flex-start;
`;

const LoadMoreText = styled.span`
  ${theme.typography.bodySmall};
  margin-left: 13px;
  color: ${theme.colors.charcoal700};
`;

const StickyTableHeader = styled.thead`
  position: sticky;
  margin: 0;
  top: 0;
  z-index: 1;
`;

const StyledTable = styled.table`
  width: 100%;
  border-spacing: 0;
  text-align: left;
`;

const ScrollContainer = styled.div`
  height: 100%;
  overflow: auto;
  width: 100%;
`;

export type IssuesCustomDataViewProps = React.ComponentProps<
  NonNullable<ReferencePickerPlugin<Issue>["CustomDataView"]>
>;

const IssuesCustomDataView = withIssuesConsumer(
  ({
    disabledIds = [],
    selectedIds = [],
    entities = [],
    onLoadMore,
    onChange,
    multiple,
    loading,
    error,
    setSelection,
    onTryAgain,
  }: IssuesCustomDataViewProps) => {
    const { T } = useTranslations();
    const dataTableColumns = useTableColumns();
    const transformedEntities = useIssuesTableData(entities);

    const dataByKey = useMemo(
      () => keyBy(transformedEntities, ENTITIES_MAPPER_KEY),
      [transformedEntities]
    );

    const columns = useMemo(
      () =>
        multiple
          ? dataTableColumns
          : dataTableColumns.filter(column => column.id !== "select"),
      [dataTableColumns, multiple]
    );

    const rowSelection = useMemo(
      () =>
        (selectedIds || []).reduce((acc: { [key: string]: boolean }, id) => {
          acc[id] = true;
          return acc;
        }, {}),
      [selectedIds]
    );

    const table = useReactTable({
      onRowSelectionChange: updater => {
        const newSelection =
          typeof updater === "function" ? updater(rowSelection) : updater;
        const changedIds = Array.from(
          new Set([...Object.keys(rowSelection), ...Object.keys(newSelection)])
        ).filter(id => rowSelection[id] !== newSelection[id]);
        changedIds.forEach(id => handleRowSelection(table.getRow(id)));
      },
      columns: columns as [],
      data: transformedEntities,
      getRowId: (row: Issue) => row?.id,
      getCoreRowModel: getCoreRowModel(),
      enableRowSelection: (row: Row<Issue>) => !disabledIds?.includes(row.id),
      enableMultiRowSelection: multiple,
      enableColumnResizing: true,
      enableSortingRemoval: false,
      columnResizeMode: "onChange",
      state: {
        rowSelection,
      },
    });

    const {
      getTableRowProps,
      getTableCellProps,
      getTableHeaderGroupProps,
      getTableHeaderProps,
    } = useTable({
      table,
    });

    const { getRowModel, getHeaderGroups } = table;

    const { rows } = getRowModel();

    const { handleScroll, virtualRows, layouts, tableRef } = useVirtualTable({
      onLoadMore,
      rowsLength: rows?.length,
      loading,
      error: !!error,
    });

    const { paddingTop, paddingBottom } = layouts;

    const handleRowSelection = ({
      getCanSelect,
      getIsSelected,
      id,
    }: Row<Issue>) => {
      if (!getCanSelect?.()) return;
      const selectedRow = dataByKey[id] as Issue;
      if (multiple) {
        onChange({
          entity: selectedRow,
          checked: !getIsSelected?.(),
        });
      } else {
        setSelection(selectedIds.includes(id) ? [] : [selectedRow]);
      }
    };

    const handleLoadMore = useCallback(
      async () => onLoadMore?.(),
      [onLoadMore]
    );

    const handleRetry = useCallback(async () => onTryAgain?.(), [onTryAgain]);

    return (
      <ScrollContainer
        onScroll={handleScroll}
        ref={tableRef}
        data-testid="reference-picker-issue-modal-scroll-container"
      >
        <StyledTable>
          <StickyTableHeader>
            {getHeaderGroups()?.map(headerGroup => (
              <TableHeaderGroup
                key={headerGroup.id}
                {...getTableHeaderGroupProps(headerGroup)}
              >
                {headerGroup.headers.map(header => (
                  <TableHeader
                    key={header.id}
                    {...getTableHeaderProps(header)}
                  />
                ))}
              </TableHeaderGroup>
            ))}
          </StickyTableHeader>
          <tbody>
            {paddingTop > 0 && (
              <TableRow style={{ height: `${paddingTop}px` }} />
            )}
            {virtualRows.map(({ index }) => {
              const row = rows[index];
              const { getVisibleCells, id, getCanSelect } = row;
              return (
                <TableRow
                  key={id}
                  {...getTableRowProps(row)}
                  onClick={() => handleRowSelection(row)}
                  disabled={!getCanSelect()}
                  role="row"
                >
                  {getVisibleCells().map(cell => (
                    <TableCell key={cell.id} {...getTableCellProps(cell)} />
                  ))}
                </TableRow>
              );
            })}
            {paddingBottom > 0 && (
              <TableRow style={{ height: `${paddingBottom}px` }} />
            )}
          </tbody>
        </StyledTable>
        {(loading || error) && (
          <StyledLoadMoreDataFooter
            isLoading={loading}
            onLoad={handleLoadMore}
            hasError={!!error}
            onRetry={handleRetry}
            renderLoading={() => (
              <>
                <ProgressRing size={ProgressRing.SIZES.SMALL} />
                <LoadMoreText>
                  {T.translate("referencePickerPlugin.fetchingMoreResults")}
                </LoadMoreText>
              </>
            )}
          />
        )}
      </ScrollContainer>
    );
  }
);

IssuesCustomDataView.displayName = "IssuesCustomDataView";

export default IssuesCustomDataView;
